<script lang="ts">
    import type { Event } from '../../api/events';
    import * as t from '../../translations/Events.ts';
    import { getEventDescriptionExcerpt, getFriendlyTimeLabel } from '../../api/events';
    import Button from '../Button.svelte';
    import CaretRightIcon from '../../icons/CaretRightIcon.svelte';
    import StarIcon from '../../icons/StarIcon.svelte';

    export let events: Event[] = [];
    export let onRegister: (event: Event) => void = () => {};

    function formatStart(dateString: string | undefined): string {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) return '';
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
        });
    }

    $: spotCount = events.length;
    $: gridCols = spotCount === 1 ? 'md:grid-cols-2' : spotCount === 2 ? 'md:grid-cols-2' : spotCount === 3 ? 'md:grid-cols-3' : 'md:grid-cols-4';
</script>

{#if events.length > 0}
    <section class="w-full" aria-label="Featured Events">
        <div class="grid grid-cols-1 sm:grid-cols-2 {gridCols} gap-4 sm:gap-3 auto-rows-[220px] sm:auto-rows-[150px] md:auto-rows-[170px]">
            {#each events as event, index}
                {@const isLargeCardDesktop = spotCount === 1 || spotCount === 2 || index === 0}
                {@const cardSpan = spotCount === 2 ? 'sm:col-span-1 sm:row-span-2' : (isLargeCardDesktop ? 'sm:col-span-2 sm:row-span-2' : '')}
                {@const friendlyTime = getFriendlyTimeLabel(event.startDate)}
                <div
                    class="event-spotlight-card-shell group cursor-pointer rounded-xl overflow-hidden shadow-lg relative transition-all duration-300 hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] focus:ring-2 focus:ring-white focus:ring-offset-2 focus:outline-none {cardSpan}"
                    on:click={() => onRegister(event)}
                    on:keydown={(e) => e.key === 'Enter' && onRegister(event)}
                    role="button"
                    tabindex="0"
                    aria-label="Featured event: {event.title}"
                >
                    {#if event.imageUrl}
                        <img
                            src={event.imageUrl}
                            alt={event.title}
                            class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <div class="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent sm:from-black/80 sm:via-black/30"></div>
                    {:else}
                        <div class="absolute inset-0 bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900 transition-all duration-300 group-hover:from-slate-700 group-hover:via-slate-600 group-hover:to-slate-800"></div>
                    {/if}

                    <div class="event-spotlight-card-overlay absolute inset-0 p-4 sm:p-3 md:p-4 flex flex-col justify-end !text-white">
                        <div class="flex items-center gap-2 mb-2 sm:mb-1 md:mb-2">
                            <span class="event-spotlight-featured-badge inline-flex items-center gap-1 px-2.5 py-1 sm:px-2 sm:py-0.5 bg-white/20 backdrop-blur-sm rounded-full text-[11px] sm:text-[9px] md:text-[10px] font-medium uppercase tracking-wide">
                                <StarIcon width="10" height="10" class="sm:w-[8px] sm:h-[8px] md:w-[9px] md:h-[9px]" aria-hidden="true" />
                                <span>Featured</span>
                            </span>
                            {#if friendlyTime}
                                <span class="event-spotlight-time-badge inline-flex items-center gap-1 px-2.5 py-1 sm:px-2 sm:py-0.5 rounded-full text-[11px] sm:text-[9px] md:text-[10px] font-medium {friendlyTime.type === 'urgent' ? 'bg-red-500/90' : friendlyTime.type === 'soon' ? 'bg-amber-500/90' : 'bg-blue-500/90'}">
                                    <span>{friendlyTime.label}</span>
                                </span>
                            {/if}
                        </div>

                        <h3 class="event-spotlight-card-title !font-bold !leading-tight !text-white line-clamp-2 !text-xl mb-2 {isLargeCardDesktop ? 'sm:!text-lg md:!text-xl sm:mb-1 md:mb-2' : 'sm:!text-xs md:!text-sm sm:mb-0'}">
                            {event.title}
                        </h3>

                        <div class="sm:hidden">
                            <p class="event-spotlight-date !text-white/80 !text-xs !leading-4 mb-1">
                                {formatStart(event.startDate)}{event.location ? ` \u2022 ${event.location}` : ''}
                            </p>
                            <p class="event-spotlight-description !text-white/80 !text-sm !leading-5 mb-3 line-clamp-1">{getEventDescriptionExcerpt(event.description, 100)}</p>
                            <div class="flex flex-col gap-3">
                                <Button
                                    type="navigation"
                                    class="event-spotlight-cta-button !bg-white !text-gray-900 !border-white hover:!bg-gray-200 active:!bg-gray-300 !min-h-[48px] !text-sm !leading-5 transition-colors w-full"
                                >
                                    <div class="flex gap-2 justify-center">
                                        <div>{event.isRegistered ? t.View_Details_Button() : t.Register_Button()}</div>
                                        <div class="my-auto">
                                            <CaretRightIcon color="currentColor" aria-hidden="true" />
                                        </div>
                                    </div>
                                </Button>
                            </div>
                        </div>

                        <div class="hidden sm:block">
                            <!--
                                Small (1-column) spotlight desktop variant intentionally has no
                                inline date/description/CTA -- the mobile-style block above already
                                covers it. Only the large variant renders the inline metadata row.
                            -->
                            {#if isLargeCardDesktop}
                                <p class="event-spotlight-date !text-white/80 !text-[10px] md:!text-xs !leading-4 mb-1">
                                    {formatStart(event.startDate)}{event.location ? ` \u2022 ${event.location}` : ''}
                                </p>
                                <p class="event-spotlight-description !text-white/80 !text-xs md:!text-sm !leading-5 mb-2 md:mb-3 line-clamp-2">{getEventDescriptionExcerpt(event.description, 120)}</p>
                                <div class="flex flex-row flex-wrap items-center gap-2 md:gap-3">
                                    <Button
                                        type="navigation"
                                        class="event-spotlight-cta-button !bg-white !text-gray-900 !border-white hover:!bg-gray-200 active:!bg-gray-300 !min-h-[36px] md:!min-h-[40px] !text-xs md:!text-sm !leading-5 transition-colors"
                                    >
                                        <div class="flex gap-2">
                                            <div>{event.isRegistered ? t.View_Details_Button() : t.Register_Button()}</div>
                                            <div class="my-auto">
                                                <CaretRightIcon color="currentColor" aria-hidden="true" />
                                            </div>
                                        </div>
                                    </Button>
                                </div>
                            {/if}
                        </div>
                    </div>
                </div>
            {/each}
        </div>
    </section>
{/if}

<style>
    :global(.event-spotlight-card-shell) {
        font-size: 1rem !important;
        line-height: 1.5rem !important;
    }

    :global(.event-spotlight-card-overlay) {
        color: #ffffff !important;
    }

    :global(.event-spotlight-card-title) {
        color: #ffffff !important;
        font-weight: 700 !important;
    }

    :global(.event-spotlight-date),
    :global(.event-spotlight-description) {
        color: rgba(255, 255, 255, 0.8) !important;
    }

    :global(.event-spotlight-cta-button) {
        border: 1px solid #ffffff !important;
        background-color: #ffffff !important;
        color: #111827 !important;
    }
</style>

